import { tenantStorageKey } from './tenantStorage.ts'

export type OperationNotificationMode = 'in_app' | 'browser' | 'both' | 'off'
export type OperationNotificationDuration = 0 | 5000 | 10000 | 20000 | 30000
export type NotificationPresentationMode = OperationNotificationMode
export type NotificationCategory = 'ai' | 'operations' | 'monitoring' | 'backup' | 'security' | 'system'
export type NotificationTone = 'success' | 'warning' | 'error' | 'info'

export function notificationToneForEvent(eventKey: unknown): NotificationTone {
  const key = String(eventKey || '').toLowerCase()
  if (/(failed|error|down|offline|critical)/.test(key)) return 'error'
  if (/(high_|warning|attention|approval|input_required)/.test(key)) return 'warning'
  if (/(success|completed|finished|recovered|resolved)/.test(key)) return 'success'
  return 'info'
}

export type DeviceNotificationRouting = {
  foreground: Record<NotificationCategory, NotificationPresentationMode>
  backgroundSystem: Record<NotificationCategory, boolean>
}

export const OPERATION_NOTIFICATION_KEY = 'servernoxOperationNotificationMode'
export const OPERATION_NOTIFICATION_DURATION_KEY = 'servernoxOperationNotificationDuration'
export const DEVICE_NOTIFICATION_ROUTING_KEY = 'servernoxDeviceNotificationRouting'
export const OPERATION_NOTIFICATION_CHANGED_EVENT = 'servernox-operation-notification-changed'

export const NOTIFICATION_CATEGORIES: Array<{ key: NotificationCategory; title: string; description: string }> = [
  { key: 'ai', title: 'AI Coding', description: 'AI yanıtı, tamamlanma, izin ve kullanıcı etkileşimi bekleme' },
  { key: 'operations', title: 'Panel işleri', description: 'Paket, bakım, deploy, terminal ve uzun süren işlemler' },
  { key: 'monitoring', title: 'Gözlem ve incident', description: 'Sunucu, monitor, servis ve escalation olayları' },
  { key: 'backup', title: 'Yedekleme ve restore', description: 'Yedek, geri yükleme, transfer ve DR sonuçları' },
  { key: 'security', title: 'Güvenlik', description: 'Güvenlik taraması, erişim ve kritik koruma olayları' },
  { key: 'system', title: 'Sistem ve diğer', description: 'Giriş, Docker, yapılandırma ve diğer platform olayları' },
]

const DEFAULT_ROUTING: DeviceNotificationRouting = {
  foreground: {
    ai: 'in_app', operations: 'in_app', monitoring: 'in_app',
    backup: 'in_app', security: 'in_app', system: 'in_app',
  },
  backgroundSystem: {
    ai: true, operations: false, monitoring: true,
    backup: true, security: true, system: true,
  },
}

function validMode(value: unknown): value is OperationNotificationMode {
  return value === 'in_app' || value === 'browser' || value === 'both' || value === 'off'
}

/** Panel işleri varsayılan olarak işletim sistemi bildirimi üretmez. */
export function getOperationNotificationMode(storage: Pick<Storage, 'getItem'> = localStorage): OperationNotificationMode {
  const value = storage.getItem(tenantStorageKey(OPERATION_NOTIFICATION_KEY, storage))
  return validMode(value) ? value : 'in_app'
}

export function saveOperationNotificationMode(mode: OperationNotificationMode, storage: Storage = localStorage): void {
  storage.setItem(tenantStorageKey(OPERATION_NOTIFICATION_KEY, storage), validMode(mode) ? mode : 'in_app')
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(OPERATION_NOTIFICATION_CHANGED_EVENT, { detail: { mode } }))
  }
}

export function getOperationNotificationDuration(storage: Pick<Storage, 'getItem'> = localStorage): OperationNotificationDuration {
	const raw = storage.getItem(tenantStorageKey(OPERATION_NOTIFICATION_DURATION_KEY, storage))
	if (raw === null || raw === '') return 10000
	const value = Number(raw)
  return value === 0 || value === 5000 || value === 10000 || value === 20000 || value === 30000 ? value : 10000
}

export function saveOperationNotificationDuration(duration: OperationNotificationDuration, storage: Storage = localStorage): void {
  const normalized = duration === 0 || duration === 5000 || duration === 10000 || duration === 20000 || duration === 30000 ? duration : 10000
  storage.setItem(tenantStorageKey(OPERATION_NOTIFICATION_DURATION_KEY, storage), String(normalized))
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(OPERATION_NOTIFICATION_CHANGED_EVENT, { detail: { duration: normalized } }))
  }
}

export function operationNotificationChannels(mode: OperationNotificationMode): { inApp: boolean; browser: boolean } {
  return { inApp: mode === 'in_app' || mode === 'both', browser: mode === 'browser' || mode === 'both' }
}

function cleanRouting(value: unknown): DeviceNotificationRouting {
  const source = value && typeof value === 'object' ? value as Partial<DeviceNotificationRouting> : {}
  const foreground = { ...DEFAULT_ROUTING.foreground }
  const backgroundSystem = { ...DEFAULT_ROUTING.backgroundSystem }
  for (const category of NOTIFICATION_CATEGORIES) {
    const mode = source.foreground?.[category.key]
    if (validMode(mode)) foreground[category.key] = mode
    const background = source.backgroundSystem?.[category.key]
    if (typeof background === 'boolean') backgroundSystem[category.key] = background
  }
  return { foreground, backgroundSystem }
}

/** Cihaz ve aktif tenant için bağlama duyarlı bildirim yönlendirmesi. */
export function getDeviceNotificationRouting(storage: Pick<Storage, 'getItem'> = localStorage): DeviceNotificationRouting {
  try {
    const raw = storage.getItem(tenantStorageKey(DEVICE_NOTIFICATION_ROUTING_KEY, storage))
    if (!raw) {
      // Eski tek ayarın panel işleri tercihini kaybetmeden yeni modele geç.
      const migrated = cleanRouting(null)
      migrated.foreground.operations = getOperationNotificationMode(storage)
      migrated.backgroundSystem.operations = operationNotificationChannels(migrated.foreground.operations).browser
      return migrated
    }
    return cleanRouting(JSON.parse(raw))
  } catch {
    return cleanRouting(null)
  }
}

export function saveDeviceNotificationRouting(routing: DeviceNotificationRouting, storage: Storage = localStorage): void {
  const normalized = cleanRouting(routing)
  storage.setItem(tenantStorageKey(DEVICE_NOTIFICATION_ROUTING_KEY, storage), JSON.stringify(normalized))
  // Eski kod ve eski APK'lar panel işleri için davranışı korusun.
  saveOperationNotificationMode(normalized.foreground.operations, storage)
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(OPERATION_NOTIFICATION_CHANGED_EVENT, { detail: { routing: normalized } }))
    void syncNotificationRoutingToServiceWorker(normalized)
  }
}

export function notificationCategoryForEvent(eventKey: unknown): NotificationCategory {
  const key = String(eventKey || '').toLowerCase()
  if (key.startsWith('ai.')) return 'ai'
  if (key.startsWith('backup.') || key.startsWith('backup_')) return 'backup'
  if (key.startsWith('incident.') || key.startsWith('server.') || key.startsWith('monitor.')) return 'monitoring'
  if (key.startsWith('security.') || key.startsWith('auth_failed') || key.startsWith('firewall.')) return 'security'
  if (key.startsWith('operation.') || key.startsWith('job.') || key.startsWith('deploy.')) return 'operations'
  return 'system'
}

/** Güvenli iç bildirim hedefini doğrular ve eski WAF rotasını günceller. */
export function notificationTargetUrl(value: unknown, fallback = '/notifications/history', eventKey: unknown = ''): string {
  if (typeof value !== 'string' || !value.startsWith('/') || value.startsWith('//')) return fallback
  const criticalWaf = String(eventKey || '') === 'security.waf.critical_threat'
  if (value === '/waf' || value.startsWith('/waf?') || value.startsWith('/waf#')) {
    const canonical = '/security/waf' + value.slice(4)
    return criticalWaf && canonical === '/security/waf' ? '/security/waf?tab=threats' : canonical
  }
  if (criticalWaf && value === '/security/waf') return '/security/waf?tab=threats'
  return value
}

export function notificationPresentation(
  routing: DeviceNotificationRouting,
  category: NotificationCategory,
  foreground: boolean,
): { inApp: boolean; system: boolean } {
  if (!foreground) return { inApp: false, system: routing.backgroundSystem[category] !== false }
  const channels = operationNotificationChannels(routing.foreground[category] || 'in_app')
  return { inApp: channels.inApp, system: channels.browser }
}

/** Görünür PWA penceresi ile service worker aynı cihaz yönlendirmesini kullanır. */
export async function syncNotificationRoutingToServiceWorker(routing = getDeviceNotificationRouting()): Promise<void> {
  const normalized = cleanRouting(routing)
  const nativePreference = (globalThis as typeof globalThis & { window?: Window & { SCPNative?: { setPreference?: (key: string, value: string) => Promise<unknown> } } }).window?.SCPNative?.setPreference
  if (typeof nativePreference === 'function') {
    try { await nativePreference('notificationRouting', JSON.stringify(normalized)) } catch {}
  }
  if (typeof navigator === 'undefined' || !navigator.serviceWorker) return
  const message = { type: 'SERVERNOX_NOTIFICATION_ROUTING', routing: normalized }
  navigator.serviceWorker.controller?.postMessage(message)
  try { (await navigator.serviceWorker.ready).active?.postMessage(message) } catch {}
}
